import { AppShell, Burger, useMantineColorScheme } from '@mantine/core';
import { LogIn, LogOut, Menu, Moon, Search, Sun } from 'lucide-react';
import { FormEvent, useState } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';

interface AppHeaderProps {
  mobileOpened: boolean;
  toggleMobile: () => void;
  desktopOpened: boolean;
  toggleDesktop: () => void;
  authMode?: boolean;
}

export const AppHeader = ({
  mobileOpened,
  toggleMobile,
  desktopOpened,
  toggleDesktop,
  authMode = false,
}: AppHeaderProps) => {
  const { colorScheme, setColorScheme } = useMantineColorScheme();
  const user = useAuthStore((state) => state.user);
  const logout = useAuthStore((state) => state.logout);
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const isDark = colorScheme === 'dark';

  const submitSearch = (event: FormEvent) => {
    event.preventDefault();
    const value = query.trim();
    if (value) navigate(`/search?title=${encodeURIComponent(value)}`);
  };

  return (
    <AppShell.Header className="atlas-header">
      <div className="atlas-header__brand">
        {!authMode && (
          <>
            <Burger opened={mobileOpened} onClick={toggleMobile} hiddenFrom="md" size="sm" aria-label="Toggle navigation" />
            <button className="plain-icon-button desktop-rail-toggle" onClick={toggleDesktop} aria-label={desktopOpened ? 'Collapse navigation' : 'Expand navigation'}>
              <Menu size={18} />
            </button>
          </>
        )}
        <Link to="/" className="luna-brand">
          <span className="luna-mark"><Moon size={16} /></span>
          <span>Luna</span>
        </Link>
      </div>

      {!authMode && (
        <form className="atlas-header__search" onSubmit={submitSearch} role="search">
          <Search size={16} />
          <input value={query} onChange={(event) => setQuery(event.currentTarget.value)} placeholder="Search the library..." aria-label="Search the library" />
          <kbd>Enter</kbd>
        </form>
      )}

      <div className="atlas-header__actions">
        {!authMode && (
          <nav className="header-links" aria-label="Quick links">
            <NavLink to="/about">About</NavLink>
            {user?.role === 'administrator' && <NavLink to="/admin">Manage</NavLink>}
          </nav>
        )}
        <button className="plain-icon-button" onClick={() => setColorScheme(isDark ? 'light' : 'dark')} aria-label={`Use ${isDark ? 'light' : 'dark'} theme`}>
          {isDark ? <Sun size={17} /> : <Moon size={17} />}
        </button>
        {!authMode && (user && user.role !== 'guest' ? (
          <button className="account-action" onClick={() => { logout(); navigate('/'); }}><LogOut size={15} /><span>Sign out</span></button>
        ) : (
          <Link className="account-action" to="/login"><LogIn size={15} /><span>Sign in</span></Link>
        ))}
      </div>
    </AppShell.Header>
  );
};
